// Today, during the trip (DESIGN.md, Views, "Today, during the trip"; features/on-the-road.md): the day from
// my side. What Today picks, from the trip as loaded, the day it is on this device, the time and where I am
// (while Where I am is on): the day and where we are, Next up, what's on today, what we want around us,
// what waits for my rating here, tonight's stay and tomorrow. Plain functions with type-only imports, so the
// server-side tests can use them too (like deck.ts); Today.tsx draws it.
//
// It never needs an itinerary and is never rigid: with nothing on a day it shows what we want around us,
// nothing it picks is late or overdue, and nothing moves by itself.
import { dayDate, dayNumber, daySpan, locationWindow, tripStart } from "../../src/days.ts";
import { daysUntil, mineOrNobodys, openDated } from "../../src/waits.ts";
import type { Item, Person, When } from "../../src/web/types.ts";
import { zonedToUtc } from "../../src/zoned.ts";
import { type DeckArea, deckAreas } from "./deck.ts";
import { weekday } from "./format.ts";
import { distanceM, type LngLat, point } from "./geo.ts";
import { type GroupHead, listLayout, NEAR_STAY_M } from "./maplist.ts";
import { inPlan } from "./plan.ts";

// ---------- The day ----------

// Whether Today is offered on this day: from the day before the trip to the day after, the window in
// which people can share where they are (src/days.ts, `locationWindow`), by this device's own date.
export function inTodayWindow(dates: When, date: string): boolean {
  const window = locationWindow(dates);
  return window !== null && date >= window.opens && date <= window.closes;
}

// "Day 15 of 36 · Sat 12 Dec"; the day before the trip and the day after say so. The eyebrow over the
// day's area (drawn in capitals).
export function dayLine(dates: When, date: string): string {
  const start = tripStart(dates);
  const last = dates.precision === "days" ? dates.end : start;
  const n = start ? dayNumber(date, start) : null;
  const of = start && last ? dayNumber(last, start) : null;
  const which =
    n === null || of === null
      ? null
      : n < 1
        ? "The day before the trip"
        : n > of
          ? "The day after the trip"
          : `Day ${n} of ${of}`;
  return [which, weekday(date)].filter(Boolean).join(" · ");
}

// The day after a date, as a date.
export function nextDay(date: string): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
}

// ---------- Where we stay ----------

// Somewhere we stay: chosen, booked or stayed at, as the map's list and the deck count it.
const SETTLED = new Set(["planned", "booked", "done"]);
const FIRM: Record<string, number> = { done: 2, booked: 2, planned: 1 };

// Where we are on a day: the group of stays the map's list heads with the stays' city ("Osaka"), or
// "Near <stay>" for a stay with no city. `name` is what a sentence says ("Before you leave Kanazawa"):
// the city, else the stay's own name.
export interface Area {
  key: string;
  label: string;
  name: string;
  stays: Item[];
}

// The stay for a night (night n is day n's night) among stays with their nights, the firmest, then the
// one that starts last, when two cover it.
function stayOn(stays: { item: Item; first: number; last: number }[], n: number) {
  return (
    stays
      .filter((s) => (s.first === s.last ? s.first === n : s.first <= n && n < s.last))
      .sort((a, b) => (FIRM[b.item.status] ?? 0) - (FIRM[a.item.status] ?? 0) || b.first - a.first)[0] ?? null
  );
}

// A stay's check-in and check-out, when its `when` has the times (features/stays-and-moves.md: a booked
// stay's times, from the booking); null while it has days only.
export interface StayTimes {
  in: { date: string; time: string; at: number };
  out: { date: string; time: string; at: number };
}
export function stayTimes(stay: Item): StayTimes | null {
  const w = stay.when;
  if (!w || (w.precision !== "exact" && w.precision !== "window") || !w.end) return null;
  const outTz = w.precision === "exact" ? (w.end_tz ?? w.tz) : w.tz;
  return {
    in: { date: w.start.slice(0, 10), time: w.start.slice(11, 16), at: zonedToUtc(w.start, w.tz).getTime() },
    out: { date: w.end.slice(0, 10), time: w.end.slice(11, 16), at: zonedToUtc(w.end, outTz).getTime() },
  };
}

// ---------- What's on today ----------

// A time today: its local time and the moment it is, from a `when` with a time (exact or window) that
// starts on `date`.
function timedOn(when: When | undefined, date: string): { time: string; at: number } | null {
  if (!when || (when.precision !== "exact" && when.precision !== "window")) return null;
  if (when.start.slice(0, 10) !== date) return null;
  return { time: when.start.slice(11, 16), at: zonedToUtc(when.start, when.tz).getTime() };
}

// Whether it's on a day: a time that starts that day, a part of that day, that day, or days that cover it.
function onDay(when: When | undefined, n: number, date: string, start: string | null): boolean {
  if (!when) return false;
  if (when.precision === "exact" || when.precision === "window") return when.start.slice(0, 10) === date;
  const span = daySpan(when, start);
  return span !== null && span.first <= n && n <= span.last;
}

// Booked for the whole of a day: a place or goal booked, with that day (or days covering it) and no time or part
// of it (a theme park's day, a day tour). It fills the day: Next up says so, and what we want around us waits. A
// pass booked over the day (an info) is the day's pass line, never the day itself.
function bookedAllDay(item: Item, n: number, start: string | null): boolean {
  if (item.status !== "booked" || (item.type !== "place" && item.type !== "goal")) return false;
  const w = item.when;
  if (!w || (w.precision !== "day" && w.precision !== "days")) return false;
  const span = daySpan(w, start);
  return span !== null && span.first <= n && n <= span.last;
}

const PARTS = ["morning", "afternoon", "evening", "night"] as const;
// Not happening (any more): left off what's on a day.
const OFF = new Set(["dropped", "cancelled", "option", "not_chosen"]);
// A fact covering the day (a rail pass): only once it's more than an idea.
const NOT_FACT = new Set(["idea", ...OFF]);

export type TodayRow =
  // Something on today: at a time, in a part of the day, or any time.
  | { kind: "item"; item: Item; time: string | null; part: (typeof PARTS)[number] | null }
  // A stay's check-out or check-in today, from its times.
  | { kind: "checkout" | "checkin"; item: Item; time: string }
  // A pass or a fact covering today, as one line: "Hokuriku Arch Pass · day 6 of 7".
  | { kind: "pass"; item: Item; day: number; of: number }
  // An open to-do of mine or nobody's, due today or before.
  | { kind: "todo"; item: Item };

// The next timed thing still to come today, at most one: a move with a time, something booked with a time,
// a stay's check-out or check-in from its times; else something booked for the whole day.
export type NextUp =
  | { kind: "timed"; item: Item; time: string; at: number }
  | { kind: "checkout" | "checkin"; item: Item; time: string; at: number }
  | { kind: "allday"; item: Item };

// ---------- Around us ----------

// Wanting it: Yes, then Maybe. A No isn't.
export type Tier = "yes" | "maybe";
const RANK: Record<Tier, number> = { yes: 1, maybe: 0 };
// Still worth doing: not done, skipped, cancelled or dropped (missed is: it can still happen).
const STILL = new Set(["idea", "planned", "booked", "missed"]);

// A place or goal someone wants, with whose wanting it is (the most anyone said, and who said it), how far
// it is from where the list measures (null when that has no place), and for a goal, its nearest candidate.
export interface AroundRow {
  item: Item;
  tier: Tier;
  who: Person[];
  m: number | null;
  via: Item | null;
}

// One list of what we want around us: from where I am, or around a stay's area; yes, then maybe, nearest
// first in each (since 2026-10-21 a maybe is a row like the rest: it took in what was "if time").
export interface AroundList {
  area: Area | null;
  rows: AroundRow[];
}

// "Around you" while Where I am is on; "Around Osaka" from tonight's stay; on a moving day, the morning's
// area open and the evening's folded. `full`: something booked fills the day, and it all waits folded.
export interface Around {
  mode: "you" | "area" | "moving";
  lists: AroundList[];
  full: boolean;
}

// ---------- Today ----------

export interface TodayInput {
  items: readonly Item[];
  // The trip's members now: only they have a say in what we want.
  members: readonly Person[];
  you: string;
  dates: When;
  // This device's today, a plain date.
  date: string;
  now: Date;
  // Where I am, while Where I am is on.
  me: LngLat | null;
  // The hour on this device's clock (0 to 23): from 18:00, How was today?
  hour: number;
}

// From this hour of the evening, Today asks how the day was.
export const EVENING_HOUR = 18;

export interface TodayPicks {
  date: string;
  dayLine: string;
  // Where we are: last night's area (this morning's) and tonight's, each when there's a stay; a moving day
  // has both, and they differ.
  morning: Area | null;
  evening: Area | null;
  moving: boolean;
  next: NextUp | null;
  rows: TodayRow[];
  around: Around | null;
  // What waits for my rating in the area I'm in: the deck's area (deck.ts) and where it is among them.
  rate: { area: DeckArea; index: number } | null;
  tonight: { stay: Item; night: number; nights: number; checkIn: string | null; area: Area } | null;
  // Tomorrow, while it's in the trip: its first fixed thing, else the stay it moves on to, else nothing.
  tomorrow: {
    date: string;
    first: { item: Item; time: string | null; kind: "item" | "checkout" | "checkin" | "allday" } | null;
    onTo: Area | null;
  } | null;
  // From 18:00, How was today?: what was on today (places, goals and journeys), each to answer; null before.
  howWasToday: Item[] | null;
}

export function pickToday(input: TodayInput): TodayPicks {
  const { members, you, dates, date, now, me, hour } = input;
  const start = tripStart(dates);
  const plan = inPlan([...input.items]);
  const n = (start ? dayNumber(date, start) : null) ?? 0;
  const last = dates.precision === "days" ? dates.end : start;
  const lastN = start && last ? (dayNumber(last, start) ?? 0) : 0;

  // Where we stay, by night, and the areas the map's list groups them in.
  const stays = plan.flatMap((item) => {
    if (item.type !== "stay" || !SETTLED.has(item.status)) return [];
    const span = daySpan(item.when, start);
    return span ? [{ item, first: span.first, last: span.last }] : [];
  });
  const heads = listLayout(
    stays.map((s) => s.item).filter((s) => point(s) !== null),
    start,
  ).heads.filter((h) => h.stays.length > 0);
  const areaOfStay = (stay: Item): Area => {
    const head = heads.find((h) => h.stays.some((s) => s.id === stay.id));
    return head ? areaOf(head) : alone(stay);
  };
  const lastNight = stayOn(stays, n - 1);
  const tonightStay = stayOn(stays, n);
  const morning = lastNight ? areaOfStay(lastNight.item) : null;
  const evening = tonightStay ? areaOfStay(tonightStay.item) : null;
  const moving = morning !== null && evening !== null && morning.key !== evening.key;

  // What's on today.
  const todayItems = plan.filter(
    (i) => !OFF.has(i.status) && ["place", "goal", "move"].includes(i.type) && onDay(i.when, n, date, start),
  );
  const timed: TodayRow[] = [];
  const parts: TodayRow[] = [];
  const anyTime: TodayRow[] = [];
  const instants = new Map<TodayRow, number>();
  for (const item of todayItems) {
    const t = timedOn(item.when, date);
    if (t) {
      const row: TodayRow = { kind: "item", item, time: t.time, part: null };
      instants.set(row, t.at);
      timed.push(row);
    } else if (item.when?.precision === "part") parts.push({ kind: "item", item, time: null, part: item.when.part });
    else anyTime.push({ kind: "item", item, time: null, part: null });
  }
  for (const s of stays) {
    const times = stayTimes(s.item);
    if (!times) continue;
    if (times.out.date === date) {
      const row: TodayRow = { kind: "checkout", item: s.item, time: times.out.time };
      instants.set(row, times.out.at);
      timed.push(row);
    }
    if (times.in.date === date) {
      const row: TodayRow = { kind: "checkin", item: s.item, time: times.in.time };
      instants.set(row, times.in.at);
      timed.push(row);
    }
  }
  timed.sort((a, b) => (instants.get(a) ?? 0) - (instants.get(b) ?? 0));
  const partRank = (r: TodayRow) => (r.kind === "item" && r.part ? PARTS.indexOf(r.part) : 0);
  parts.sort((a, b) => partRank(a) - partRank(b) || byTitle(a, b));
  anyTime.sort(byTitle);
  const passes: TodayRow[] = plan.flatMap((item) => {
    if (item.type !== "info" || NOT_FACT.has(item.status)) return [];
    const span = daySpan(item.when, start);
    if (!span || span.first > n || span.last < n) return [];
    return [{ kind: "pass" as const, item, day: n - span.first + 1, of: span.last - span.first + 1 }];
  });
  const inTrip = new Set(members.map((m) => m.id));
  const todos: TodayRow[] = input.items
    .filter((i) => openDated(i) && mineOrNobodys(i, you, inTrip) && daysUntil(i.due as string, date) <= 0)
    .sort((a, b) => (a.due ?? "").localeCompare(b.due ?? "") || a.title.localeCompare(b.title))
    .map((item) => ({ kind: "todo" as const, item }));
  const rows = [...timed, ...parts, ...anyTime, ...passes, ...todos];

  // Next up: the soonest timed thing still to come (a move, something booked, a stay's check-out or
  // check-in), else something booked for the whole day.
  const allDay = plan.find((i) => bookedAllDay(i, n, start)) ?? null;
  const coming = timed.filter((r) => {
    const at = instants.get(r) ?? 0;
    if (at <= now.getTime()) return false;
    if (r.kind !== "item") return true;
    return r.item.type === "move" || r.item.status === "booked";
  });
  const soonest = coming[0];
  const next: NextUp | null = soonest
    ? soonest.kind === "item"
      ? { kind: "timed", item: soonest.item, time: soonest.time ?? "", at: instants.get(soonest) ?? 0 }
      : soonest.kind === "checkout" || soonest.kind === "checkin"
        ? { kind: soonest.kind, item: soonest.item, time: soonest.time, at: instants.get(soonest) ?? 0 }
        : null
    : allDay
      ? { kind: "allday", item: allDay }
      : null;

  // What we want around us.
  const candidates = plan.filter(
    (i) => (i.type === "place" || i.type === "goal") && STILL.has(i.status) && daySpan(i.when, start) === null,
  );
  const children = new Map<string, Item[]>();
  for (const i of plan)
    if (i.parent && i.type === "place" && !OFF.has(i.status) && point(i))
      children.set(i.parent, [...(children.get(i.parent) ?? []), i]);
  // The area a point is in: the nearest stay's, within the map's list's reach of it.
  const settledAt = stays.flatMap((s) => {
    const p = point(s.item);
    return p ? [{ item: s.item, at: p }] : [];
  });
  const areaAt = (p: LngLat): Area | null => {
    let best: { item: Item; m: number } | null = null;
    for (const s of settledAt) {
      const m = distanceM(s.at, p);
      if (m <= NEAR_STAY_M && (!best || m < best.m)) best = { item: s.item, m };
    }
    return best ? areaOfStay(best.item) : null;
  };
  // A list measured from `from`, of what's in `keep`.
  const list = (area: Area | null, from: LngLat | null, keep: (p: LngLat) => boolean): AroundList => {
    const all: AroundRow[] = [];
    for (const item of candidates) {
      const said = wanted(item, members);
      if (!said) continue;
      const via = item.type === "goal" ? nearest(children.get(item.id) ?? [], from) : null;
      const at = item.type === "goal" ? (via ? point(via) : point(item)) : point(item);
      if (!at || !keep(at)) continue;
      all.push({ item, tier: said.tier, who: said.who, m: from ? distanceM(from, at) : null, via });
    }
    const order = (a: AroundRow, b: AroundRow) =>
      RANK[b.tier] - RANK[a.tier] ||
      (a.m ?? Number.POSITIVE_INFINITY) - (b.m ?? Number.POSITIVE_INFINITY) ||
      a.item.title.localeCompare(b.item.title);
    return { area, rows: all.sort(order) };
  };
  const inArea = (area: Area) => (p: LngLat) => areaAt(p)?.key === area.key;
  const stayPoint = (area: Area | null, stay: Item | null) => (stay ? point(stay) : null) ?? firstPoint(area);
  let around: Around | null = null;
  if (me) around = { mode: "you", lists: [list(null, me, (p) => distanceM(me, p) <= NEAR_STAY_M)], full: false };
  else if (moving && morning && evening)
    around = {
      mode: "moving",
      lists: [
        list(morning, stayPoint(morning, lastNight?.item ?? null), inArea(morning)),
        list(evening, stayPoint(evening, tonightStay?.item ?? null), inArea(evening)),
      ],
      full: false,
    };
  else {
    const area = evening ?? morning;
    const stay = (tonightStay ?? lastNight)?.item ?? null;
    if (area) around = { mode: "area", lists: [list(area, stayPoint(area, stay), inArea(area))], full: false };
  }
  if (around) around.full = allDay !== null;

  // What waits for my rating here: the deck's area for where I am (while Where I am is on), else tonight's
  // (this morning's on the last day).
  const here = (me ? areaAt(me) : null) ?? evening ?? morning;
  const areas = deckAreas(input.items, members, you, start);
  const index = here ? areas.findIndex((a) => a.key === here.key) : -1;
  const rate = index >= 0 ? { area: areas[index] as DeckArea, index } : null;

  // Tonight's stay.
  const tonight =
    tonightStay && evening
      ? {
          stay: tonightStay.item,
          night: n - tonightStay.first + 1,
          nights: Math.max(1, tonightStay.last - tonightStay.first),
          checkIn: tonightStay.first === n ? (stayTimes(tonightStay.item)?.in.time ?? null) : null,
          area: evening,
        }
      : null;

  // Tomorrow, while it's in the trip.
  let tomorrow: TodayPicks["tomorrow"] = null;
  if (start && n + 1 <= lastN) {
    const date2 = dayDate(n + 1, start) ?? nextDay(date);
    const allDay2 = plan.find((i) => bookedAllDay(i, n + 1, start));
    const firsts: { item: Item; time: string; at: number; kind: "item" | "checkout" | "checkin" }[] = [];
    for (const item of plan) {
      if (OFF.has(item.status) || item.type === "stay" || item.type === "task" || item.type === "info") continue;
      const t = timedOn(item.when, date2);
      if (t) firsts.push({ item, ...t, kind: "item" });
    }
    for (const s of stays) {
      const times = stayTimes(s.item);
      if (times?.out.date === date2) firsts.push({ item: s.item, ...times.out, kind: "checkout" });
      if (times?.in.date === date2) firsts.push({ item: s.item, ...times.in, kind: "checkin" });
    }
    firsts.sort((a, b) => a.at - b.at);
    const first = allDay2
      ? { item: allDay2, time: null, kind: "allday" as const }
      : firsts[0]
        ? { item: firsts[0].item, time: firsts[0].time, kind: firsts[0].kind }
        : null;
    const tomorrowStay = stayOn(stays, n + 1);
    const then = tomorrowStay ? areaOfStay(tomorrowStay.item) : null;
    const onTo = then && evening && then.key !== evening.key ? then : null;
    tomorrow = { date: date2, first, onTo };
  }

  // How was today?, from the evening on: what was on today, as it is now (done, missed, or neither yet).
  // What has a time still to come (tonight's train) isn't asked about until its time has come.
  const asked =
    hour >= EVENING_HOUR
      ? rows.flatMap((r) =>
          r.kind === "item" && r.item.status !== "skipped" && (instants.get(r) ?? 0) <= now.getTime() ? [r.item] : [],
        )
      : null;

  return {
    date,
    dayLine: dayLine(dates, date),
    morning,
    evening,
    moving,
    next,
    rows,
    around,
    rate,
    tonight,
    tomorrow,
    howWasToday: asked,
  };
}

// ---------- Helpers ----------

function areaOf(head: GroupHead): Area {
  const city = head.stays[0]?.where?.city?.trim();
  return { key: head.key, label: head.label, name: city || (head.stays[0]?.title ?? head.label), stays: head.stays };
}

// A stay with no place: an area of its own, as the map's list would name it.
function alone(stay: Item): Area {
  const city = stay.where?.city?.trim();
  return { key: `near:${stay.id}`, label: city || `Near ${stay.title}`, name: city || stay.title, stays: [stay] };
}

function firstPoint(area: Area | null): LngLat | null {
  for (const s of area?.stays ?? []) {
    const p = point(s);
    if (p) return p;
  }
  return null;
}

const byTitle = (a: TodayRow, b: TodayRow) => a.item.title.localeCompare(b.item.title);

// The most anyone in the trip wants it, and who said that much; null when nobody wants it (a No
// isn't wanting).
function wanted(item: Item, members: readonly Person[]): { tier: Tier; who: Person[] } | null {
  let tier: Tier | null = null;
  for (const x of item.interest) {
    if (!members.some((m) => m.id === x.person)) continue;
    const t = x.tier as Tier | null;
    if (t && t in RANK && (tier === null || RANK[t] > RANK[tier])) tier = t;
  }
  if (tier === null) return null;
  const best = tier;
  return { tier, who: members.filter((m) => item.interest.some((x) => x.person === m.id && x.tier === best)) };
}

// A goal's candidate nearest a point (the first with a place, with none to measure from).
function nearest(candidates: Item[], from: LngLat | null): Item | null {
  let best: { item: Item; m: number } | null = null;
  for (const c of candidates) {
    const p = point(c);
    if (!p) continue;
    const m = from ? distanceM(from, p) : 0;
    if (!best || m < best.m) best = { item: c, m };
  }
  return best?.item ?? null;
}

// ---------- Words ----------

// How long until something, as people say it: "in 45 min", "in 2 h 20 min", "in 3 h".
export function untilWords(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes < 60) return `in ${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `in ${h} h${m ? ` ${m} min` : ""}`;
}

const listNames = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// Whose wanting it is, from my side, as a person's rating is said short: "You said yes", "You and Monalisa
// said yes", "You said maybe", "Mei said maybe" (until 2026-10-23 "You must" and "Maybe for you").
export function whoseWords(row: Pick<AroundRow, "tier" | "who">, you: string): string {
  const names = [...row.who]
    .sort((a, b) => Number(b.id === you) - Number(a.id === you))
    .map((p) => (p.id === you ? "You" : p.name));
  return `${listNames(names)} said ${row.tier}`;
}

// "Nothing's fixed today. Here's what you both want around you." With nothing wanted around us, only the
// first sentence.
export function nothingFixedWords(members: number, anything: boolean): string {
  if (!anything) return "Nothing's fixed today.";
  const who = members <= 1 ? "you" : members === 2 ? "you both" : "you all";
  return `Nothing's fixed today. Here's what ${who} want around you.`;
}
